import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Search, Users } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useCreateBooking, useUpdateBooking, useSearchGuests, BookingFormData, Booking } from '@/hooks/useBookings';
import { useProperties } from '@/hooks/useProperties';
import { useDebounce } from '@/hooks/useDebounce';
import { supabase } from '@/integrations/supabase/client';
import CustomDatePicker from '@/components/CustomDatePicker';
import { formatDateForUrl, parseDateFromDatabase } from '@/utils/dateParser';

interface BookingFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking?: Booking | null;
  preselectedPropertyId?: string;
  preselectedCheckInDate?: Date | null;
  onBookingCreated?: () => void;
}

const BookingFormModal = ({ 
  isOpen, 
  onClose, 
  booking, 
  preselectedPropertyId,
  preselectedCheckInDate,
  onBookingCreated 
}: BookingFormModalProps) => {
  const [checkInDate, setCheckInDate] = useState<Date>();
  const [checkOutDate, setCheckOutDate] = useState<Date>();
  const [guestSearchTerm, setGuestSearchTerm] = useState('');
  const [selectedGuest, setSelectedGuest] = useState<any>(null);
  const [showGuestResults, setShowGuestResults] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [selectedOriginId, setSelectedOriginId] = useState('');
  const [stayCompleted, setStayCompleted] = useState(false);
  
  const debouncedSearchTerm = useDebounce(guestSearchTerm, 500);
  const { data: guestResults = [] } = useSearchGuests(debouncedSearchTerm);
  const { data: properties = [] } = useProperties();
  const createBookingMutation = useCreateBooking();
  const updateBookingMutation = useUpdateBooking();

  const { register, handleSubmit, formState: { errors }, setValue, watch, reset } = useForm<BookingFormData>();

  // Determinar si estamos editando
  const isEditing = !!booking;
  const currentMutation = isEditing ? updateBookingMutation : createBookingMutation;

  // Cargar orígenes
  const [origins, setOrigins] = useState<any[]>([]);
  useEffect(() => {
    const fetchOrigins = async () => {
      const { data } = await supabase.from('origins').select('*').order('name');
      setOrigins(data || []);
    };
    fetchOrigins();
  }, []);

  // Función para limpiar completamente el formulario
  const clearForm = () => {
    console.log('🧹 Clearing form data');
    reset();
    setCheckInDate(undefined);
    setCheckOutDate(undefined);
    setGuestSearchTerm('');
    setSelectedGuest(null);
    setSelectedPropertyId('');
    setSelectedOriginId('');
    setStayCompleted(false);
    setShowGuestResults(false);
  };

  // Cargar datos si estamos editando, limpiar si es nueva reservación
  useEffect(() => {
    console.log('🔄 BookingFormModal: Loading booking data', { 
      booking, 
      isOpen, 
      preselectedPropertyId,
      preselectedCheckInDate 
    });
    
    if (isOpen) {
      if (booking) {
        // CORRECCIÓN: Usar parseDateFromDatabase para respetar fechas exactas
        const checkInFromDB = parseDateFromDatabase(booking.check_in);
        const checkOutFromDB = parseDateFromDatabase(booking.check_out);
        
        console.log('📅 Loading booking dates from DB:', {
          raw_check_in: booking.check_in,
          raw_check_out: booking.check_out,
          parsed_check_in: checkInFromDB?.toDateString(),
          parsed_check_out: checkOutFromDB?.toDateString()
        });
        
        if (checkInFromDB) setCheckInDate(checkInFromDB);
        if (checkOutFromDB) setCheckOutDate(checkOutFromDB);
        
        setGuestSearchTerm(booking.guest_name);
        setSelectedGuest({
          id: booking.guest_id,
          name: booking.guest_name,
          email: booking.guest_email,
          phone: booking.guest_phone,
        });
        
        setSelectedPropertyId(booking.property_id);
        setSelectedOriginId(booking.origin_id);
        setStayCompleted(booking.stay_completed || false);
        
        setValue('property_id', booking.property_id);
        setValue('origin_id', booking.origin_id);
        setValue('guest_name', booking.guest_name);
        setValue('guest_email', booking.guest_email || '');
        setValue('guest_phone', booking.guest_phone || '');
        setValue('total_guests', booking.total_guests);
        setValue('children', booking.children || 0);
        setValue('total_payment', booking.total_payment);
        setValue('cleaning_fee', booking.cleaning_fee || 0);
        setValue('base_payment', booking.base_payment || 0);
        setValue('pets_fee', booking.pets_fee || 0);
        setValue('early_checkin_fee', booking.early_checkin_fee || 0);
        setValue('late_checkout_fee', booking.late_checkout_fee || 0);
        setValue('other_fees', booking.other_fees || 0);
        setValue('payout', booking.payout || 0);
        setValue('stay_completed', booking.stay_completed || false);
        setValue('note', booking.note || '');
      } else {
        clearForm();
        
        if (preselectedPropertyId) {
          console.log('🏠 Setting preselected property:', preselectedPropertyId);
          setSelectedPropertyId(preselectedPropertyId);
          setValue('property_id', preselectedPropertyId);
        }
        
        if (preselectedCheckInDate) {
          console.log('📅 Setting preselected check-in date:', preselectedCheckInDate);
          setCheckInDate(preselectedCheckInDate);
        }
      }
    }
  }, [booking, setValue, reset, isOpen, preselectedPropertyId, preselectedCheckInDate]);

  useEffect(() => {
    if (!isOpen) {
      clearForm();
    }
  }, [isOpen]);

  const handleGuestSelect = (guest: any) => {
    setSelectedGuest(guest);
    setGuestSearchTerm(guest.name);
    setValue('guest_name', guest.name);
    setValue('guest_email', guest.email || '');
    setValue('guest_phone', guest.phone || '');
    setValue('guest_id', guest.id);
    setShowGuestResults(false);
  };

  const handleGuestSearchChange = (value: string) => {
    setGuestSearchTerm(value);
    setValue('guest_name', value);
    if (value !== selectedGuest?.name) {
      setSelectedGuest(null);
      setValue('guest_id', undefined);
      setValue('guest_email', '');
      setValue('guest_phone', '');
    }
    setShowGuestResults(true);
  };

  const handleCheckInChange = (date: Date | undefined) => {
    setCheckInDate(date);
    if (date && checkOutDate && checkOutDate <= date) {
      setCheckOutDate(undefined);
    }
  };

  const handleCheckOutChange = (date: Date | undefined) => {
    setCheckOutDate(date);
  };

  const handlePropertyChange = (value: string) => {
    console.log('🏠 Property changed to:', value);
    setSelectedPropertyId(value);
    setValue('property_id', value);
  };

  const handleOriginChange = (value: string) => {
    console.log('📍 Origin changed to:', value);
    setSelectedOriginId(value);
    setValue('origin_id', value);
  };

  const handleStayCompletedChange = (checked: boolean) => {
    setStayCompleted(checked);
    setValue('stay_completed', checked);
  };

  const onSubmit = (data: BookingFormData) => {
    if (!checkInDate || !checkOutDate) {
      alert('Por favor selecciona las fechas de llegada y salida');
      return;
    }

    // CORRECCIÓN: Usar formatDateForUrl que mantiene la fecha exacta
    const formData: BookingFormData = {
      ...data,
      check_in: formatDateForUrl(checkInDate),
      check_out: formatDateForUrl(checkOutDate),
      guest_id: selectedGuest?.id,
      stay_completed: stayCompleted,
    };

    console.log('📅 Submitting booking with corrected dates:', {
      originalCheckIn: checkInDate.toDateString(),
      originalCheckOut: checkOutDate.toDateString(),
      formattedCheckIn: formData.check_in,
      formattedCheckOut: formData.check_out,
      stayCompleted: formData.stay_completed,
      note: formData.note
    });

    if (isEditing && booking) {
      // Actualizar reservación existente
      updateBookingMutation.mutate({ id: booking.id, bookingData: formData }, {
        onSuccess: () => {
          if (onBookingCreated) {
            onBookingCreated();
          }
          onClose();
        },
      });
    } else {
      // Crear nueva reservación
      createBookingMutation.mutate(formData, {
        onSuccess: () => {
          if (onBookingCreated) {
            onBookingCreated();
          }
          onClose();
        },
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>
              {booking ? 'Editar Reservación' : 'Nueva Reservación'}
            </DialogTitle>
            {booking && (
              <Badge 
                className={booking.paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
              >
                {booking.paid ? 'Pagado' : 'Pendiente de pago'}
              </Badge>
            )}
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Propiedad */}
          <div className="space-y-2">
            <Label htmlFor="property_id">Propiedad *</Label>
            <Select value={selectedPropertyId} onValueChange={handlePropertyChange}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona una propiedad" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((property) => (
                  <SelectItem key={property.id} value={property.id}>
                    {property.shortName || property.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.property_id && (
              <p className="text-red-500 text-sm">Este campo es requerido</p>
            )}
          </div>

          {/* Origen */}
          <div className="space-y-2">
            <Label htmlFor="origin_id">Origen *</Label>
            <Select value={selectedOriginId} onValueChange={handleOriginChange}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona el origen" />
              </SelectTrigger>
              <SelectContent>
                {origins.map((origin) => (
                  <SelectItem key={origin.id} value={origin.id}>
                    {origin.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.origin_id && (
              <p className="text-red-500 text-sm">Este campo es requerido</p>
            )}
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Llegada *</Label>
              <CustomDatePicker 
                selected={checkInDate} 
                onSelect={handleCheckInChange}
                placeholder="Seleccionar fecha"
                title="Fecha de entrada"
                isAdminContext={true}
              />
            </div>

            <div className="space-y-2">
              <Label>Salida *</Label>
              <CustomDatePicker 
                selected={checkOutDate} 
                onSelect={handleCheckOutChange}
                placeholder="Seleccionar fecha"
                title="Fecha de salida"
                isAdminContext={true}
              />
            </div>
          </div>

          {/* Búsqueda de Huésped */}
          <div className="space-y-2 relative">
            <Label htmlFor="guest_name">Huésped *</Label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                {...register('guest_name', { required: true })}
                value={guestSearchTerm}
                onChange={(e) => handleGuestSearchChange(e.target.value)}
                placeholder="Buscar huésped por nombre..."
                className="pl-10"
              />
            </div>
            
            {/* Resultados de búsqueda */}
            {showGuestResults && guestResults.length > 0 && guestSearchTerm.length >= 2 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {guestResults.map((guest) => (
                  <button
                    key={guest.id}
                    type="button"
                    onClick={() => handleGuestSelect(guest)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b last:border-b-0"
                  >
                    <div className="font-medium">{guest.name}</div>
                    {guest.email && <div className="text-sm text-gray-500">{guest.email}</div>}
                  </button>
                ))}
              </div>
            )}
            {errors.guest_name && (
              <p className="text-red-500 text-sm">Este campo es requerido</p>
            )}
          </div>

          {/* Email y Teléfono */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="guest_email">Email</Label>
              <Input
                {...register('guest_email')}
                type="email"
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="guest_phone">Teléfono</Label>
              <Input
                {...register('guest_phone')}
                placeholder="+52 123 456 7890"
              />
            </div>
          </div>

          {/* Huéspedes y Niños */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="total_guests">Huéspedes Totales *</Label>
              <div className="relative">
                <Users className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  {...register('total_guests', { required: true, min: 1 })}
                  type="number"
                  min="1"
                  placeholder="2"
                  className="pl-10"
                />
              </div>
              {errors.total_guests && (
                <p className="text-red-500 text-sm">Mínimo 1 huésped</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="children">Niños</Label>
              <Input
                {...register('children')}
                type="number"
                min="0"
                placeholder="0"
              />
            </div>
          </div>

          {/* Checkbox de Hospedaje Realizado */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="stay_completed"
              checked={stayCompleted}
              onCheckedChange={handleStayCompletedChange}
            />
            <Label htmlFor="stay_completed" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Hospedaje realizado
            </Label>
          </div>

          {/* Nueva Sección de Precio */}
          <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
            <h3 className="font-semibold text-lg">Precio</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="total_payment">Pago Total *</Label>
                <Input
                  {...register('total_payment', { required: true, min: 0 })}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                />
                {errors.total_payment && (
                  <p className="text-red-500 text-sm">Este campo es requerido</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="payout">Pago Recibido</Label>
                <Input
                  {...register('payout')}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="base_payment">Precio por Noches</Label>
                <Input
                  {...register('base_payment')}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cleaning_fee">Tarifa de Limpieza</Label>
                <Input
                  {...register('cleaning_fee')}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pets_fee">Tarifa de Mascotas</Label>
                <Input
                  {...register('pets_fee')}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="early_checkin_fee">Tarifa de Entrada Temprana</Label>
                <Input
                  {...register('early_checkin_fee')}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="late_checkout_fee">Tarifa de Salida Tardía</Label>
                <Input
                  {...register('late_checkout_fee')}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="other_fees">Otros Cargos</Label>
                <Input
                  {...register('other_fees')}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Campo de Notas */}
          <div className="space-y-2">
            <Label htmlFor="note">Notas</Label>
            <Textarea
              {...register('note')}
              placeholder="Agregar notas sobre la reservación..."
              className="min-h-[80px]"
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-4 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={currentMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {currentMutation.isPending ? 'Guardando...' : booking ? 'Actualizar' : 'Crear Reservación'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingFormModal;
